import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { useToast } from '../../ui/use-toast';
import { Progress } from '../../ui/progress';
import { 
  Briefcase, 
  MapPin, 
  Plus,
  Edit,
  Trash2,
  Search,
  MoreVertical,
  Building,
  Map,
  Calendar,
  Users,
  Clock,
  TrendingUp,
  FileSpreadsheet,
  Download,
  AlertTriangle,
  CheckCircle,
  Info,
  Target,
  Package,
  Activity
} from 'lucide-react';
import { Project, Location } from '../../../types';
import { apiService } from '../../../services/api';
import * as XLSX from 'xlsx';

interface ProjectsLocationsTabProps {
  projects: Project[];
  locations: Location[];
  onRefresh: () => void;
  isLoading: boolean;
  onAddProject: (project: Project) => void;
  onUpdateProject: (projectId: number, project: Project) => void;
  onRemoveProject: (projectId: number) => void;
  onAddLocation: (location: Location) => void;
  onUpdateLocation: (locationId: number, location: Location) => void;
  onRemoveLocation: (locationId: number) => void;
}

export const ProjectsLocationsTab: React.FC<ProjectsLocationsTabProps> = ({
  projects,
  locations,
  onRefresh,
  isLoading,
  onAddProject,
  onUpdateProject,
  onRemoveProject,
  onAddLocation,
  onUpdateLocation,
  onRemoveLocation
}) => {
  const [activeView, setActiveView] = useState<'projects' | 'locations'>('projects');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);
  const [showCreateLocationDialog, setShowCreateLocationDialog] = useState(false);
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [showEditLocationDialog, setShowEditLocationDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'project' | 'location', id: number, name: string } | null>(null);
  const { toast } = useToast();

  // Form states
  const [projectForm, setProjectForm] = useState({
    name: ''
  });

  const [locationForm, setLocationForm] = useState({
    name: ''
  });

  // Filter items based on search
  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLocations = locations.filter(l => 
    l.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Create project
  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) {
      toast({
        title: 'Eroare',
        description: 'Numele proiectului este obligatoriu',
        variant: 'destructive'
      });
      return;
    }

    try {
      const newProject = await apiService.createProject({
        name: projectForm.name
      });

      // Add the new project to the UI immediately
      onAddProject(newProject);

      toast({
        title: 'Succes',
        description: 'Proiect creat cu succes',
      });

      setShowCreateProjectDialog(false);
      resetProjectForm();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut crea proiectul',
        variant: 'destructive'
      });
    }
  };

  // Create location
  const handleCreateLocation = async () => {
    if (!locationForm.name.trim()) {
      toast({
        title: 'Eroare',
        description: 'Numele locației este obligatoriu',
        variant: 'destructive'
      });
      return;
    }

    try {
      const newLocation = await apiService.createLocation({
        name: locationForm.name
      });

      // Add the new location to the UI immediately
      onAddLocation(newLocation);

      toast({
        title: 'Succes',
        description: 'Locație creată cu succes',
      });

      setShowCreateLocationDialog(false);
      resetLocationForm();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut crea locația',
        variant: 'destructive'
      });
    }
  };

  // Update project
  const handleUpdateProject = async () => {
    if (!selectedProject || !projectForm.name.trim()) return;

    try {
      const updatedProject = await apiService.updateProject(selectedProject.id, {
        name: projectForm.name
      });

      // Update the project in the UI immediately
      onUpdateProject(selectedProject.id, updatedProject);

      toast({
        title: 'Succes',
        description: 'Proiect actualizat cu succes',
      });

      setShowEditProjectDialog(false);
      setSelectedProject(null);
      resetProjectForm();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut actualiza proiectul',
        variant: 'destructive'
      });
    }
  };

  // Update location
  const handleUpdateLocation = async () => {
    if (!selectedLocation || !locationForm.name.trim()) return;

    try {
      const updatedLocation = await apiService.updateLocation(selectedLocation.id, {
        name: locationForm.name
      });

      // Update the location in the UI immediately
      onUpdateLocation(selectedLocation.id, updatedLocation);

      toast({
        title: 'Succes',
        description: 'Locație actualizată cu succes',
      });

      setShowEditLocationDialog(false);
      setSelectedLocation(null);
      resetLocationForm();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut actualiza locația',
        variant: 'destructive'
      });
    }
  };

  // Delete item
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'project') {
        await apiService.deleteProject(itemToDelete.id);
        // Remove the project from the UI immediately
        onRemoveProject(itemToDelete.id);
      } else {
        await apiService.deleteLocation(itemToDelete.id);
        // Remove the location from the UI immediately
        onRemoveLocation(itemToDelete.id);
      }

      toast({
        title: 'Succes',
        description: `${itemToDelete.type === 'project' ? 'Proiect' : 'Locație'} șters cu succes`,
      });

      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: `Nu s-a putut șterge ${itemToDelete.type === 'project' ? 'proiectul' : 'locația'}`,
        variant: 'destructive'
      });
    }
  };

  // Export to Excel
  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    
    // Projects sheet
    const projectsData = projects.map(p => ({
      ID: p.id,
      Nume: p.name,
      Status: p.isActive ? 'Activ' : 'Inactiv',
      'Nr. Locații': locations.filter(l => l.projectId === p.id).length
    }));
    const wsProjects = XLSX.utils.json_to_sheet(projectsData);
    XLSX.utils.book_append_sheet(wb, wsProjects, 'Proiecte');
    
    // Locations sheet
    const locationsData = locations.map(l => ({
      ID: l.id,
      Nume: l.name,
      Proiect: projects.find(p => p.id === l.projectId)?.name || 'Fără proiect'
    }));
    const wsLocations = XLSX.utils.json_to_sheet(locationsData);
    XLSX.utils.book_append_sheet(wb, wsLocations, 'Locații');
    
    XLSX.writeFile(wb, `proiecte-locatii-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Reset forms
  const resetProjectForm = () => {
    setProjectForm({
      name: ''
    });
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: ''
    });
  };

  // Calculate statistics
  const stats = {
    totalProjects: projects.length,
    activeProjects: projects.filter(p => p.isActive).length,
    totalLocations: locations.length,
    locationsWithProjects: locations.filter(l => l.projectId).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Gestionare Proiecte și Locații</h2>
        <Button
          onClick={handleExport}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export Excel
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Proiecte</p>
                <p className="text-2xl font-bold">{stats.totalProjects}</p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Proiecte Active</p>
                <p className="text-2xl font-bold">{stats.activeProjects}</p>
                <Progress 
                  value={stats.totalProjects > 0 ? (stats.activeProjects / stats.totalProjects) * 100 : 0} 
                  className="mt-2 h-2" 
                />
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Locații</p>
                <p className="text-2xl font-bold">{stats.totalLocations}</p>
              </div>
              <MapPin className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Locații Asignate</p>
                <p className="text-2xl font-bold">{stats.locationsWithProjects}</p>
                <Progress 
                  value={stats.totalLocations > 0 ? (stats.locationsWithProjects / stats.totalLocations) * 100 : 0} 
                  className="mt-2 h-2" 
                />
              </div>
              <Target className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <Button
                size="sm"
                variant={activeView === 'projects' ? 'default' : 'ghost'}
                onClick={() => setActiveView('projects')}
                className="flex items-center gap-2"
              >
                <Briefcase className="h-4 w-4" />
                Proiecte
              </Button>
              <Button
                size="sm"
                variant={activeView === 'locations' ? 'default' : 'ghost'}
                onClick={() => setActiveView('locations')}
                className="flex items-center gap-2"
              >
                <MapPin className="h-4 w-4" />
                Locații
              </Button>
            </div>
            
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={`Caută ${activeView === 'projects' ? 'proiecte' : 'locații'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Button
              onClick={() => activeView === 'projects' ? setShowCreateProjectDialog(true) : setShowCreateLocationDialog(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {activeView === 'projects' ? 'Proiect Nou' : 'Locație Nouă'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Projects View */}
      {activeView === 'projects' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => {
            const projectLocations = locations.filter(l => l.projectId === project.id);
            
            return (
              <Card key={project.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg">{project.name}</span>
                    <Badge variant={project.isActive ? 'success' : 'secondary'}>
                      {project.isActive ? 'Activ' : 'Inactiv'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{projectLocations.length} locații</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedProject(project);
                        setProjectForm({
                          name: project.name
                        });
                        setShowEditProjectDialog(true);
                      }}
                      className="flex-1"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Editează
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setItemToDelete({ type: 'project', id: project.id, name: project.name });
                        setShowDeleteConfirm(true);
                      }}
                      className="flex-1"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Șterge
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Locations View */}
      {activeView === 'locations' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLocations.map((location) => {
            const project = projects.find(p => p.id === location.projectId);
            
            return (
              <Card key={location.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{location.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-gray-400" />
                      <span className={project ? 'font-medium' : 'text-gray-500'}>
                        {project ? project.name : 'Fără proiect asignat'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedLocation(location);
                        setLocationForm({
                          name: location.name
                        });
                        setShowEditLocationDialog(true);
                      }}
                      className="flex-1"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Editează
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setItemToDelete({ type: 'location', id: location.id, name: location.name });
                        setShowDeleteConfirm(true);
                      }}
                      className="flex-1"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Șterge
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty States */}
      {activeView === 'projects' && filteredProjects.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchQuery ? 'Nu s-au găsit proiecte' : 'Nu există proiecte încă'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowCreateProjectDialog(true)}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Creează Primul Proiect
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {activeView === 'locations' && filteredLocations.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchQuery ? 'Nu s-au găsit locații' : 'Nu există locații încă'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowCreateLocationDialog(true)}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Creează Prima Locație
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Project Dialog */}
      <Dialog open={showCreateProjectDialog} onOpenChange={setShowCreateProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Creare Proiect Nou</DialogTitle>
            <DialogDescription>
              Completați informațiile pentru noul proiect
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume proiect</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="Ex: Construcție Bloc A1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateProjectDialog(false);
              resetProjectForm();
            }}>
              Anulează
            </Button>
            <Button onClick={handleCreateProject}>
              Creează Proiect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editare Proiect</DialogTitle>
            <DialogDescription>
              Modificați informațiile proiectului
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume proiect</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="Ex: Construcție Bloc A1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditProjectDialog(false);
              setSelectedProject(null);
              resetProjectForm();
            }}>
              Anulează
            </Button>
            <Button onClick={handleUpdateProject}>
              Salvează Modificări
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Location Dialog */}
      <Dialog open={showCreateLocationDialog} onOpenChange={setShowCreateLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Creare Locație Nouă</DialogTitle>
            <DialogDescription>
              Completați informațiile pentru noua locație
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume locație</Label>
              <Input
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                placeholder="Ex: Șantier Nord"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateLocationDialog(false);
              resetLocationForm();
            }}>
              Anulează
            </Button>
            <Button onClick={handleCreateLocation}>
              Creează Locație
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={showEditLocationDialog} onOpenChange={setShowEditLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editare Locație</DialogTitle>
            <DialogDescription>
              Modificați informațiile locației
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume locație</Label>
              <Input
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                placeholder="Ex: Șantier Nord"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditLocationDialog(false);
              setSelectedLocation(null);
              resetLocationForm();
            }}>
              Anulează
            </Button>
            <Button onClick={handleUpdateLocation}>
              Salvează Modificări
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Sunteți sigur că doriți să ștergeți {itemToDelete?.type === 'project' ? 'proiectul' : 'locația'} "{itemToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Această acțiune nu poate fi anulată. 
              {itemToDelete?.type === 'project' && ' Toate locațiile asociate vor rămâne fără proiect.'}
            </AlertDescription>
          </Alert>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setItemToDelete(null);
            }}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};